import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ShieldOff, FolderOpen } from 'lucide-react';
import toast from 'react-hot-toast';

const API_BASE = import.meta.env.VITE_API_URL || '';

declare global {
  interface Window {
    kakao: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  }
}

type FieldSurvey = {
  id: number;
  groupId: number | null;
  group: { id: number; name: string } | null;
  address: string;
  dongName: string | null;
  memo: string | null;
  isAccessRestricted: boolean;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  _count: { units: number };
};

type FieldSurveyGroup = {
  id: number;
  name: string;
  parentId: number | null;
  polygon: string | null;
  _count: { surveys: number; children: number };
  children?: FieldSurveyGroup[];
};

type SurveyCluster = {
  key: string;
  lat: number;
  lng: number;
  surveys: FieldSurvey[];
};

const loadKakaoMapsSdk = async (): Promise<void> => {
  const apiKey = import.meta.env.VITE_KAKAO_APPKEY;
  if (!apiKey) throw new Error('카카오맵 API 키가 설정되지 않았습니다.');
  if (window.kakao && window.kakao.maps) return;

  const existingScript = document.querySelector<HTMLScriptElement>('script[data-kakao-map-sdk="true"]');
  if (existingScript) {
    await new Promise<void>((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('SDK 로드 실패')), { once: true });
    });
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('data-kakao-map-sdk', 'true');
  script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&autoload=false`;
  script.async = true;

  const promise = new Promise<void>((resolve, reject) => {
    script.onload = () => { window.kakao.maps.load(() => resolve()); };
    script.onerror = () => reject(new Error('SDK 로드 실패'));
  });
  document.head.appendChild(script);
  await promise;
};

const createMarkerSvg = (color: string, unitCount: number): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
    <path d="M14 0C6.3 0 0 6.3 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 6.3 21.7 0 14 0z" fill="${color}"/>
    <circle cx="14" cy="14" r="6" fill="white"/>
    <text x="14" y="17" text-anchor="middle" font-size="9" font-weight="bold" fill="${color}">${unitCount}</text>
  </svg>`;
};

const getClusterMarkerColor = (surveys: FieldSurvey[], selectedIds: Set<number>): string => {
  const anySelected = surveys.some(s => selectedIds.has(s.id));
  if (anySelected) return '#22c55e';
  const anyRestricted = surveys.some(s => s.isAccessRestricted);
  return anyRestricted ? '#ef4444' : '#3b82f6';
};

const buildClusters = (surveys: FieldSurvey[]): SurveyCluster[] => {
  const map = new Map<string, FieldSurvey[]>();
  for (const s of surveys) {
    const key = `${s.latitude},${s.longitude}`;
    const arr = map.get(key);
    if (arr) arr.push(s);
    else map.set(key, [s]);
  }
  const clusters: SurveyCluster[] = [];
  map.forEach((clusterSurveys, key) => {
    clusters.push({
      key,
      lat: clusterSurveys[0]!.latitude!,
      lng: clusterSurveys[0]!.longitude!,
      surveys: clusterSurveys,
    });
  });
  return clusters;
};

const getClusterLabel = (surveys: FieldSurvey[], subGroupIds: Set<number>): string | null => {
  // 서브 그룹에 속한 조사만 라벨 대상
  const subGroupNames = surveys
    .filter(s => s.groupId && subGroupIds.has(s.groupId))
    .map(s => s.group?.name)
    .filter(Boolean);
  if (subGroupNames.length === 0) return null;
  const unique = [...new Set(subGroupNames)];
  if (unique.length === 1) return unique[0]!;
  if (unique.length <= 2) return unique.join('/');
  return '혼합';
};

const FieldSurveyMapPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const mapRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markersRef = useRef<Map<string, any>>(new Map());
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const labelsRef = useRef<Map<string, any>>(new Map());
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const polygonsRef = useRef<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pinMarkerRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapInstanceRef = useRef<any>(null);
  const selectModeRef = useRef(false);
  const pinModeRef = useRef(false);
  const [selectedCluster, setSelectedCluster] = useState<SurveyCluster | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupParentId, setNewGroupParentId] = useState<number | null>(null);
  const [mapVersion, setMapVersion] = useState(0);

  // 핀 찍기 모드
  const pinSurveyId = searchParams.get('pin') ? parseInt(searchParams.get('pin')!) : null;
  const focusSurveyId = searchParams.get('focus') ? parseInt(searchParams.get('focus')!) : null;
  const [pinCoords, setPinCoords] = useState<{ lat: number; lng: number } | null>(null);

  selectModeRef.current = selectMode;
  pinModeRef.current = !!pinSurveyId;

  const { data } = useQuery({
    queryKey: ['field-surveys'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/field-surveys`);
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    },
  });

  const { data: groupsData } = useQuery({
    queryKey: ['field-survey-groups'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/field-survey-groups`);
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    },
  });
  const groups: FieldSurveyGroup[] = groupsData?.data || [];
  const topGroups = groups.filter(g => g.parentId === null);

  const bulkGroupMutation = useMutation({
    mutationFn: async ({ surveyIds, groupId }: { surveyIds: number[]; groupId: number | null }) => {
      const res = await fetch(`${API_BASE}/api/field-surveys/bulk-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ surveyIds, groupId }),
      });
      if (!res.ok) throw new Error('실패했습니다');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-surveys'] });
      queryClient.invalidateQueries({ queryKey: ['field-surveys-history'] });
      queryClient.invalidateQueries({ queryKey: ['field-survey-groups'] });
      toast.success('그룹이 변경되었습니다');
      setSelectMode(false);
      setSelectedIds(new Set());
      setSelectedCluster(null);
      // 지도 강제 재생성 (라벨/폴리곤 갱신)
      setTimeout(() => setMapVersion(v => v + 1), 500);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveCoordsMutation = useMutation({
    mutationFn: async ({ id, latitude, longitude }: { id: number; latitude: number; longitude: number }) => {
      const res = await fetch(`${API_BASE}/api/field-surveys/${id}/coordinates`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude, longitude }),
      });
      if (!res.ok) throw new Error('좌표 저장 실패');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-surveys'] });
      queryClient.invalidateQueries({ queryKey: ['field-surveys-history'] });
      toast.success('좌표가 저장되었습니다');
      setSearchParams({});
      setPinCoords(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const surveys: FieldSurvey[] = data?.data || [];
  const surveysWithCoords = surveys.filter(s => s.latitude && s.longitude);
  const clusters = useMemo(() => buildClusters(surveysWithCoords), [surveysWithCoords]);
  const pinSurvey = pinSurveyId ? surveys.find(s => s.id === pinSurveyId) : null;

  // 메인 지도 초기화
  useEffect(() => {
    if (!mapRef.current) return;

    let isMounted = true;

    const init = async () => {
      try {
        await loadKakaoMapsSdk();
        if (!isMounted || !mapRef.current) return;

        // 기존 마커 + 라벨 + 폴리곤 정리
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current.clear();
        labelsRef.current.forEach(label => label.setMap(null));
        labelsRef.current.clear();
        polygonsRef.current.forEach(p => p.setMap(null));
        polygonsRef.current = [];
        if (pinMarkerRef.current) { pinMarkerRef.current.setMap(null); pinMarkerRef.current = null; }

        const kakao = window.kakao;

        // 기존 지도의 중심/줌 저장 (갱신 시 위치 유지)
        const prevMap = mapInstanceRef.current;
        const prevCenter = prevMap ? prevMap.getCenter() : null;
        const prevLevel = prevMap ? prevMap.getLevel() : null;

        // 핀 찍기 모드이면서 좌표 있는 조사가 없어도 지도는 표시
        const defaultCenter = prevCenter
          ? prevCenter
          : clusters.length > 0
            ? new kakao.maps.LatLng(clusters[0]!.lat, clusters[0]!.lng)
            : new kakao.maps.LatLng(37.5665, 126.978);

        const map = new kakao.maps.Map(mapRef.current, {
          center: defaultCenter,
          level: prevLevel ?? 5,
        });
        mapInstanceRef.current = map;

        if (!prevCenter && clusters.length > 1) {
          const bounds = new kakao.maps.LatLngBounds();
          clusters.forEach(c => bounds.extend(new kakao.maps.LatLng(c.lat, c.lng)));
          map.setBounds(bounds, 80);
        }

        // 서브 그룹 ID 집합 (라벨 표시용)
        const subGroupIds = new Set<number>();
        for (const g of topGroups) {
          for (const sub of (g.children || [])) subGroupIds.add(sub.id);
        }

        // 클러스터별 마커 생성
        clusters.forEach(cluster => {
          const position = new kakao.maps.LatLng(cluster.lat, cluster.lng);
          const totalUnits = cluster.surveys.reduce((sum, s) => sum + s._count.units, 0);
          const color = getClusterMarkerColor(cluster.surveys, new Set());
          const svg = createMarkerSvg(color, totalUnits);
          const markerImage = new kakao.maps.MarkerImage(
            `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
            new kakao.maps.Size(28, 36),
            { offset: new kakao.maps.Point(14, 36) }
          );

          const marker = new kakao.maps.Marker({ map, position, image: markerImage });
          markersRef.current.set(cluster.key, marker);

          // 서브 그룹 라벨 오버레이
          const label = getClusterLabel(cluster.surveys, subGroupIds);
          if (label) {
            const labelOverlay = new kakao.maps.CustomOverlay({
              position,
              content: `<div style="font-size:10px;color:#6b7280;text-align:center;white-space:nowrap;font-weight:500;pointer-events:none;">${label}</div>`,
              yAnchor: -0.3,
              zIndex: 1,
            });
            labelOverlay.setMap(map);
            labelsRef.current.set(cluster.key, labelOverlay);
          }

          kakao.maps.event.addListener(marker, 'click', () => {
            if (pinModeRef.current) return;
            if (selectModeRef.current) {
              if (cluster.surveys.length === 1) {
                // 단일 핀은 바로 토글
                setSelectedIds(prev => {
                  const next = new Set(prev);
                  const id = cluster.surveys[0]!.id;
                  if (next.has(id)) next.delete(id); else next.add(id);
                  return next;
                });
              } else {
                // 클러스터는 패널 표시로 개별 선택
                setSelectedCluster(prev => prev?.key === cluster.key ? null : cluster);
              }
            } else {
              setSelectedCluster(prev => prev?.key === cluster.key ? null : cluster);
            }
          });
        });

        // 서브 그룹별 Polygon 렌더링
        const polygonColors = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#ec4899', '#06b6d4'];
        let colorIdx = 0;
        for (const g of topGroups) {
          for (const sub of (g.children || [])) {
            if (!sub.polygon) continue;
            try {
              const data = JSON.parse(sub.polygon);
              const polys = data.polygon || [];
              for (const poly of polys) {
                const path = poly.points.map((p: { x: number; y: number }) =>
                  new kakao.maps.LatLng(p.y, p.x)
                );
                const color = polygonColors[colorIdx % polygonColors.length]!;
                const polygon = new kakao.maps.Polygon({
                  map,
                  path,
                  strokeWeight: 2,
                  strokeColor: color,
                  strokeOpacity: 0.8,
                  fillColor: color,
                  fillOpacity: 0.15,
                });
                polygonsRef.current.push(polygon);
              }
              colorIdx++;
            } catch { /* invalid polygon json */ }
          }
        }

        // 포커스 모드: 특정 조사로 줌인
        if (focusSurveyId) {
          const focusSurvey = surveysWithCoords.find(s => s.id === focusSurveyId);
          if (focusSurvey) {
            const pos = new kakao.maps.LatLng(focusSurvey.latitude, focusSurvey.longitude);
            map.setCenter(pos);
            map.setLevel(3);
            // 해당 클러스터 자동 선택
            const focusCluster = clusters.find(c =>
              c.surveys.some(s => s.id === focusSurveyId)
            );
            if (focusCluster) setSelectedCluster(focusCluster);
          }
        }

        // 핀 찍기 모드: 기존 좌표가 있으면 해당 위치로 이동 + 기존 핀 표시
        if (pinModeRef.current && pinSurvey?.latitude && pinSurvey?.longitude) {
          const existingPos = new kakao.maps.LatLng(pinSurvey.latitude, pinSurvey.longitude);
          map.setCenter(existingPos);
          map.setLevel(3);
          setPinCoords({ lat: pinSurvey.latitude, lng: pinSurvey.longitude });

          const pinSvg = createMarkerSvg('#f59e0b', 0).replace(
            '<text x="14" y="17" text-anchor="middle" font-size="9" font-weight="bold" fill="#f59e0b">0</text>',
            '<text x="14" y="18" text-anchor="middle" font-size="12" font-weight="bold" fill="#f59e0b">+</text>'
          );
          const pinImage = new kakao.maps.MarkerImage(
            `data:image/svg+xml;charset=utf-8,${encodeURIComponent(pinSvg)}`,
            new kakao.maps.Size(28, 36),
            { offset: new kakao.maps.Point(14, 36) }
          );
          pinMarkerRef.current = new kakao.maps.Marker({ map, position: existingPos, image: pinImage });
        }

        // 핀 찍기 모드: 지도 클릭으로 좌표 설정
        kakao.maps.event.addListener(map, 'click', (mouseEvent: { latLng: { getLat: () => number; getLng: () => number } }) => {
          if (!pinModeRef.current) return;

          const lat = mouseEvent.latLng.getLat();
          const lng = mouseEvent.latLng.getLng();
          setPinCoords({ lat, lng });

          // 기존 핀 마커 제거 후 새 위치에 생성
          if (pinMarkerRef.current) pinMarkerRef.current.setMap(null);

          const svg = createMarkerSvg('#f59e0b', 0).replace(
            '<text x="14" y="17" text-anchor="middle" font-size="9" font-weight="bold" fill="#f59e0b">0</text>',
            '<text x="14" y="18" text-anchor="middle" font-size="12" font-weight="bold" fill="#f59e0b">+</text>'
          );
          const markerImage = new kakao.maps.MarkerImage(
            `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
            new kakao.maps.Size(28, 36),
            { offset: new kakao.maps.Point(14, 36) }
          );
          const marker = new kakao.maps.Marker({
            map,
            position: mouseEvent.latLng,
            image: markerImage,
          });
          pinMarkerRef.current = marker;
        });
      } catch (err) {
        if (isMounted) setMapError(err instanceof Error ? err.message : '지도 로드 실패');
      }
    };

    init();
    return () => { isMounted = false; };
  }, [clusters.length, !!pinSurveyId, focusSurveyId, mapVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // 선택 상태 변경 시 마커 색상 업데이트
  useEffect(() => {
    const kakao = window.kakao;
    if (!kakao?.maps) return;

    markersRef.current.forEach((marker, clusterKey) => {
      const cluster = clusters.find(c => c.key === clusterKey);
      if (!cluster) return;
      const totalUnits = cluster.surveys.reduce((sum, s) => sum + s._count.units, 0);
      const color = getClusterMarkerColor(cluster.surveys, selectedIds);
      const svg = createMarkerSvg(color, totalUnits);
      const image = new kakao.maps.MarkerImage(
        `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
        new kakao.maps.Size(28, 36),
        { offset: new kakao.maps.Point(14, 36) }
      );
      marker.setImage(image);
    });
  }, [selectedIds, selectMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkGroup = (groupId: number | null) => {
    bulkGroupMutation.mutate({ surveyIds: Array.from(selectedIds), groupId });
  };

  const handleCreateGroupAndAssign = async () => {
    if (!newGroupName.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/api/field-survey-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newGroupName.trim(), parentId: newGroupParentId }),
      });
      if (!res.ok) { const e = await res.json(); toast.error(e.error || '그룹 생성 실패'); return; }
      const created = await res.json();
      bulkGroupMutation.mutate({
        surveyIds: Array.from(selectedIds),
        groupId: created.data.id,
      });
      setShowGroupPicker(false);
      setNewGroupName('');
    } catch {
      toast.error('그룹 생성 실패');
    }
  };

  const handleExitPinMode = () => {
    setSearchParams({});
    setPinCoords(null);
    if (pinMarkerRef.current) { pinMarkerRef.current.setMap(null); pinMarkerRef.current = null; }
  };

  const handleSavePin = () => {
    if (!pinSurveyId || !pinCoords) return;
    saveCoordsMutation.mutate({ id: pinSurveyId, latitude: pinCoords.lat, longitude: pinCoords.lng });
  };

  // 핀 찍기 모드 여부
  const isPinMode = !!pinSurveyId;

  return (
    <div className="page-narrow" style={{ maxWidth: '100%', padding: 0 }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-100">
        <button onClick={() => {
          if (isPinMode) handleExitPinMode();
          else if (selectMode) { setSelectMode(false); setSelectedIds(new Set()); }
          else navigate('/field-survey/history');
        }}
          className="p-2 -ml-2 text-gray-400 active:text-gray-600 rounded-xl">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold text-gray-900">
            {isPinMode ? '핀 찍기' : selectMode ? '핀 선택' : '조사 지도'}
          </h1>
          <p className="text-xs text-gray-400">
            {isPinMode
              ? (pinSurvey ? `${pinSurvey.address}${pinSurvey.dongName ? ` ${pinSurvey.dongName}` : ''}` : '조사를 찾을 수 없습니다')
              : selectMode
                ? `${selectedIds.size}건 선택 · ${surveysWithCoords.filter(s => selectedIds.has(s.id)).reduce((sum, s) => sum + s._count.units, 0)}세대`
                : `${surveysWithCoords.length}건 표시`}
          </p>
        </div>
        {!isPinMode && (
          <button
            onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); setSelectedCluster(null); }}
            className={`btn-sm flex items-center gap-1 ${selectMode ? 'btn-primary' : 'btn-secondary'}`}>
            <FolderOpen size={14} />
            {selectMode ? '취소' : '선택'}
          </button>
        )}
      </div>

      {/* 핀 찍기 안내 배너 */}
      {isPinMode && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-100">
          <p className="text-xs text-amber-700">지도를 탭하여 위치를 지정하세요</p>
        </div>
      )}

      {/* Map */}
      {mapError ? (
        <div className="flex items-center justify-center h-[60vh] text-gray-400 text-sm">{mapError}</div>
      ) : !isPinMode && surveysWithCoords.length === 0 ? (
        <div className="flex items-center justify-center h-[60vh] text-gray-300 text-sm">
          좌표가 있는 조사 데이터가 없습니다
        </div>
      ) : (
        <div ref={mapRef} className="w-full" style={{ height: 'calc(100vh - 120px)' }} />
      )}

      {/* 핀 찍기 하단 액션 바 */}
      {isPinMode && pinCoords && (
        <div className="fixed bottom-[calc(64px+env(safe-area-inset-bottom,0px)+8px)] sm:bottom-2 left-2 right-2 z-30">
          <div className="card card-body max-w-page mx-auto shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {pinSurvey?.address}{pinSurvey?.dongName ? ` ${pinSurvey.dongName}` : ''}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {pinCoords.lat.toFixed(6)}, {pinCoords.lng.toFixed(6)}
                </p>
              </div>
              <button
                onClick={handleSavePin}
                disabled={saveCoordsMutation.isPending}
                className="btn-sm btn-primary ml-2">
                {saveCoordsMutation.isPending ? '저장중...' : '좌표 저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 클러스터 정보 패널 (일반 모드) */}
      {!isPinMode && selectedCluster && (
        <div className="fixed bottom-[calc(64px+env(safe-area-inset-bottom,0px)+8px)] sm:bottom-2 left-2 right-2 z-30">
          <div className="card card-body max-w-page mx-auto shadow-lg">
            {selectedCluster.surveys.length === 1 ? (
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-gray-900">{selectedCluster.surveys[0]!.address}</p>
                    {selectedCluster.surveys[0]!.isAccessRestricted && <ShieldOff size={14} className="text-red-400" />}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {selectedCluster.surveys[0]!.dongName && `${selectedCluster.surveys[0]!.dongName} · `}
                    {selectedCluster.surveys[0]!._count.units}호 · {new Date(selectedCluster.surveys[0]!.createdAt).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div className="flex gap-1.5 ml-2">
                  <button onClick={() => navigate(`/field-survey/map?pin=${selectedCluster.surveys[0]!.id}`)}
                    className="btn-sm btn-secondary text-xs">핀 수정</button>
                  <button onClick={() => navigate(`/field-survey/${selectedCluster.surveys[0]!.id}`)}
                    className="btn-sm btn-primary text-xs">편집</button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-gray-900 mb-2">{selectedCluster.surveys[0]!.address}</p>
                <div className="space-y-1.5">
                  {selectedCluster.surveys.map(survey => (
                    <div key={survey.id} className="flex items-center gap-1.5 py-1.5 px-2 bg-gray-50 rounded-lg">
                      {selectMode && (
                        <button onClick={() => setSelectedIds(prev => {
                          const next = new Set(prev);
                          if (next.has(survey.id)) next.delete(survey.id); else next.add(survey.id);
                          return next;
                        })}
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                            selectedIds.has(survey.id) ? 'bg-brand-500 border-brand-500' : 'border-gray-300'
                          }`}>
                          {selectedIds.has(survey.id) && <span className="text-white text-[10px] font-bold">✓</span>}
                        </button>
                      )}
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <span className="text-xs font-medium text-gray-700 truncate">
                          {survey.dongName || '본동'}
                        </span>
                        <span className="badge-blue">{survey._count.units}호</span>
                        {survey.isAccessRestricted && <ShieldOff size={12} className="text-red-400 flex-shrink-0" />}
                      </div>
                      {!selectMode && (
                        <>
                          <select
                            value={survey.groupId ?? ''}
                            onChange={e => {
                              const val = e.target.value;
                              bulkGroupMutation.mutate({
                                surveyIds: [survey.id],
                                groupId: val === '' ? null : parseInt(val),
                              });
                            }}
                            className={`text-[10px] rounded px-1 py-0.5 border-0 appearance-none cursor-pointer flex-shrink-0 ${
                              survey.group ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-200 text-gray-400'
                            }`}
                          >
                            <option value="">미분류</option>
                            {topGroups.map(g => (
                              <optgroup key={g.id} label={g.name}>
                                <option value={g.id}>{g.name}</option>
                                {g.children?.map(sub => (
                                  <option key={sub.id} value={sub.id}>└ {sub.name}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          <button onClick={() => navigate(`/field-survey/map?pin=${survey.id}`)}
                            className="btn-sm btn-secondary text-xs flex-shrink-0">핀</button>
                          <button onClick={() => navigate(`/field-survey/${survey.id}`)}
                            className="btn-sm btn-primary text-xs flex-shrink-0">편집</button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 그룹 할당 액션 바 (선택 모드) */}
      {!isPinMode && selectMode && selectedIds.size > 0 && !selectedCluster && (
        <div className="fixed bottom-[calc(64px+env(safe-area-inset-bottom,0px)+8px)] sm:bottom-2 left-2 right-2 z-30">
          <div className="card card-body max-w-page mx-auto shadow-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {selectedIds.size}건 · {surveysWithCoords.filter(s => selectedIds.has(s.id)).reduce((sum, s) => sum + s._count.units, 0)}세대
              </span>
              <select
                onChange={e => {
                  const val = e.target.value;
                  if (val === '') return;
                  if (val === '__new__') { setShowGroupPicker(true); return; }
                  handleBulkGroup(val === 'null' ? null : parseInt(val));
                  e.target.value = '';
                }}
                className="text-sm border border-gray-200 rounded-lg h-9 px-3"
                defaultValue=""
              >
                <option value="" disabled>그룹 선택</option>
                <option value="null">미분류</option>
                {topGroups.map(g => (
                  <optgroup key={g.id} label={g.name}>
                    <option value={g.id}>{g.name} (전체)</option>
                    {g.children?.map(sub => (
                      <option key={sub.id} value={sub.id}>└ {sub.name}</option>
                    ))}
                  </optgroup>
                ))}
                <option value="__new__">+ 새 서브 그룹</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* 새 그룹 생성 모달 */}
      {showGroupPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl p-5 mx-4 w-full max-w-sm shadow-xl">
            <h3 className="text-sm font-bold text-gray-900 mb-3">새 서브 그룹 만들기</h3>
            <select
              value={newGroupParentId ?? ''}
              onChange={e => setNewGroupParentId(e.target.value ? parseInt(e.target.value) : null)}
              className="input-field h-10 text-sm mb-2"
            >
              <option value="">상위 그룹 선택</option>
              {topGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <input
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              placeholder="서브 그룹 이름 (예: 1구역)"
              className="input-field h-10 text-sm mb-3"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleCreateGroupAndAssign(); }}
            />
            <div className="flex gap-2">
              <button onClick={() => { setShowGroupPicker(false); setNewGroupName(''); setNewGroupParentId(null); }}
                className="btn-sm btn-secondary flex-1">취소</button>
              <button
                onClick={handleCreateGroupAndAssign}
                disabled={!newGroupName.trim() || !newGroupParentId || bulkGroupMutation.isPending}
                className="btn-sm btn-primary flex-1">
                만들고 할당
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldSurveyMapPage;
